import type { Shift, Weekday } from '../types';
import { paidHoursRounded } from './nzRosterBreaks';
import { rosterRoleCategoryRank, type RosterRoleCategory } from './rosterRoleOrder';

const WEEK_DAYS: Weekday[] = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

const DAY_SHORT: Record<Weekday, string> = {
  monday: 'Mon',
  tuesday: 'Tue',
  wednesday: 'Wed',
  thursday: 'Thu',
  friday: 'Fri',
  saturday: 'Sat',
  sunday: 'Sun',
};

export interface RosterExportStaffRow {
  name: string;
  /** Role section label (Manager, Chef, …). */
  roleLabel?: string;
  roleCategory?: RosterRoleCategory;
  /** Shifts keyed by weekday (same shape as roster grid). */
  shiftsByDay: Partial<Record<Weekday, Shift[]>>;
}

export interface RosterExportInput {
  weekLabel: string;
  weekStartMs: number;
  status?: string;
  /** Day column labels (e.g. "Mon 10 Aug"). Length 7, Mon–Sun. */
  dayLabels: string[];
  staffRows: RosterExportStaffRow[];
}

function formatShiftCell(shifts: Shift[]): string {
  if (!shifts.length) return '';
  return shifts
    .slice()
    .sort((a, b) => a.start.localeCompare(b.start))
    .map((s) => `${s.start}–${s.end} (${paidHoursRounded(s)}h)`)
    .join('\n');
}

function staffRowTotal(shiftsByDay: Partial<Record<Weekday, Shift[]>>): number {
  return WEEK_DAYS.reduce(
    (sum, day) => sum + (shiftsByDay[day] ?? []).reduce((d, s) => d + paidHoursRounded(s), 0),
    0
  );
}

function dayTotalHours(staffRows: RosterExportStaffRow[], day: Weekday): number {
  return staffRows.reduce(
    (sum, row) => sum + (row.shiftsByDay[day] ?? []).reduce((d, s) => d + paidHoursRounded(s), 0),
    0
  );
}

function compareExportRows(a: RosterExportStaffRow, b: RosterExportStaffRow): number {
  const rankA = rosterRoleCategoryRank(a.roleCategory ?? 'Other');
  const rankB = rosterRoleCategoryRank(b.roleCategory ?? 'Other');
  if (rankA !== rankB) return rankA - rankB;
  const labelA = a.roleLabel ?? '';
  const labelB = b.roleLabel ?? '';
  if (labelA !== labelB) return labelA.localeCompare(labelB);
  return a.name.localeCompare(b.name);
}

function isRoleSectionRow(row: string[]): boolean {
  return row[0]?.startsWith('▸ ') === true;
}

/** Build header + body + footer rows for Excel/PDF (Employee | 7 days | Total), grouped by role. */
export function buildRosterGridAoA(input: RosterExportInput): string[][] {
  const dayHeaders = input.dayLabels.length === 7
    ? input.dayLabels
    : WEEK_DAYS.map((d) => DAY_SHORT[d]);

  const header = ['Employee', ...dayHeaders, 'Total'];
  const sorted = input.staffRows.slice().sort(compareExportRows);
  const body: string[][] = [];
  let lastSection = '';

  for (const row of sorted) {
    const section = row.roleLabel || row.roleCategory || 'Other';
    if (section !== lastSection) {
      lastSection = section;
      body.push([`▸ ${section}`, ...WEEK_DAYS.map(() => ''), '']);
    }
    const cells = WEEK_DAYS.map((day) => formatShiftCell(row.shiftsByDay[day] ?? []));
    const total = staffRowTotal(row.shiftsByDay);
    body.push([row.name, ...cells, total > 0 ? `${total.toFixed(1)}h` : '']);
  }

  const footerCells = WEEK_DAYS.map((day) => {
    const h = dayTotalHours(input.staffRows, day);
    return h > 0 ? `${h.toFixed(1)}h` : '';
  });
  const weekTotal = input.staffRows.reduce((sum, row) => sum + staffRowTotal(row.shiftsByDay), 0);
  const footer = ['Daily total', ...footerCells, weekTotal > 0 ? `${weekTotal.toFixed(1)}h` : ''];

  return [header, ...body, footer];
}

function rosterFilenameBase(weekStartMs: number): string {
  const d = new Date(weekStartMs);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `roster-${y}-${m}-${day}`;
}

/** Export weekly roster as a grid Excel workbook. */
export function exportRosterExcel(input: RosterExportInput): void {
  void import('xlsx')
    .then(({ utils, writeFile }) => {
      const aoa = buildRosterGridAoA(input);
      const worksheet = utils.aoa_to_sheet(aoa);
      worksheet['!cols'] = [
        { wch: 18 },
        ...WEEK_DAYS.map(() => ({ wch: 16 })),
        { wch: 8 },
      ];
      const workbook = utils.book_new();
      utils.book_append_sheet(workbook, worksheet, 'Roster');
      writeFile(workbook, `${rosterFilenameBase(input.weekStartMs)}.xlsx`, { bookType: 'xlsx' });
    })
    .catch((err) => {
      console.error('Roster Excel export failed', err);
      alert('Could not export Excel. Please try again.');
    });
}

/** Export weekly roster as A4 landscape PDF (fits one page for typical staff counts). */
export function exportRosterPdf(input: RosterExportInput): void {
  void Promise.all([import('jspdf'), import('jspdf-autotable')])
    .then(([jspdfMod, autoTableMod]) => {
      const { jsPDF } = jspdfMod;
      const autoTable = autoTableMod.default;
      const doc = new jsPDF({ orientation: 'landscape', format: 'a4', unit: 'mm' });
      const margin = 8;
      const pageWidth = doc.internal.pageSize.getWidth();

      doc.setFillColor(236, 72, 153);
      doc.rect(0, 0, pageWidth, 12, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(11);
      doc.text('Staff Roster', margin, 8);
      doc.setFontSize(8);
      const status = input.status ? ` · ${input.status}` : '';
      doc.text(`${input.weekLabel}${status}`, pageWidth - margin, 8, { align: 'right' });

      doc.setTextColor(0, 0, 0);
      const aoa = buildRosterGridAoA(input);
      const head = [aoa[0]!];
      const body = aoa.slice(1);

      autoTable(doc, {
        startY: 16,
        head,
        body,
        margin: { left: margin, right: margin, top: margin, bottom: margin },
        styles: {
          fontSize: 7,
          cellPadding: 1.2,
          overflow: 'linebreak',
          valign: 'top',
          lineColor: [220, 220, 220],
          lineWidth: 0.1,
        },
        headStyles: {
          fillColor: [236, 72, 153],
          textColor: [255, 255, 255],
          fontSize: 7,
          fontStyle: 'bold',
          cellPadding: 1.2,
        },
        alternateRowStyles: { fillColor: [252, 252, 252] },
        columnStyles: {
          0: { cellWidth: 28, fontStyle: 'bold' },
          8: { cellWidth: 14, halign: 'right', fontStyle: 'bold' },
        },
        didParseCell: (data) => {
          if (data.section !== 'body') return;
          const row = body[data.row.index];
          if (!row) return;
          if (data.row.index === body.length - 1) {
            data.cell.styles.fillColor = [224, 242, 254];
            data.cell.styles.fontStyle = 'bold';
            return;
          }
          if (isRoleSectionRow(row)) {
            data.cell.styles.fillColor = [241, 245, 249];
            data.cell.styles.fontStyle = 'bold';
            data.cell.styles.textColor = [51, 65, 85];
          }
        },
      });

      doc.save(`${rosterFilenameBase(input.weekStartMs)}.pdf`);
    })
    .catch((err) => {
      console.error('Roster PDF export failed', err);
      alert('Could not export PDF. Please try again.');
    });
}

/** Build day labels Mon–Sun from week start (local Monday). */
export function rosterDayLabels(weekStartMs: number): string[] {
  return WEEK_DAYS.map((day, index) => {
    const d = new Date(weekStartMs);
    d.setDate(d.getDate() + index);
    const datePart = d.toLocaleDateString('en-NZ', { day: '2-digit', month: 'short' });
    return `${DAY_SHORT[day]} ${datePart}`;
  });
}
